import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CandidateService } from "@/services/candidateService";
import useLiveResource from "@/hooks/useLiveResource";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Loader2, AlertCircle, RefreshCw, Briefcase } from "lucide-react";
import toast from "react-hot-toast";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import { MODULES } from "@/permissions/modules";
import { PERMISSIONS } from "@/permissions/constant";
import { getUserPermissions, hasPermission } from "@/permissions/rbac";
import { useAuth } from "@/hooks/useAuth";
import { LEVELS, LEVEL_LABEL, resolveLevel } from "./constants/levels";
import JobFitCard from "./component/JobFitCard";
import EmptyState from "./component/EmptyState";
import AttestationDialog from "./component/AttestationDialog";

const ACTION_BUTTON_CLASS =
  "flex items-center gap-1.5 rounded-lg bg-button-secondary px-4 py-2 text-sm font-medium text-text-base transition-colors hover:bg-button-secondary/80 disabled:cursor-not-allowed disabled:opacity-60";

const JobFit = ({ candidateId }) => {
  // This component only exists when `useVisibleTabs` kept the tab, which is
  // either because the tenant is entitled or because this candidate has a
  // stored analysis. Both states read; they differ only in what they offer.
  //
  // - Not entitled → the stored analysis renders and no control offers a new
  //   one. Withholding the module stops new spend, not reading what was
  //   already bought.
  // - Entitled but dark fleet-wide → the same, with one banner saying why,
  //   because that state is temporary and the tenant needs to know it will come
  //   back. The wording lives in `<ModuleUnavailableNotice>` below, in one
  //   place, so it cannot be printed twice.
  const { isEnabled: moduleEnabled, isAvailable: moduleAvailable } = useModuleAccess(MODULES.AI_CANDIDATE_JOB_FIT);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Applying creates an application, so it is gated on CANDIDATE_UPDATE — a
  // stricter permission than the tab's own CANDIDATE_VIEW, which only reads.
  const canApply = hasPermission(PERMISSIONS.CANDIDATE_UPDATE, getUserPermissions(user));
  const [fitData, setFitData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [levelFilter, setLevelFilter] = useState("all");

  // The recruiter's in-app attestation, step 1 of the consent chain. `null`
  // when no dialog is pending; otherwise the job being applied to.
  const [attestJob, setAttestJob] = useState(null);
  const [attestChecked, setAttestChecked] = useState(false);
  const [attestText, setAttestText] = useState("");
  const [attestSubmitting, setAttestSubmitting] = useState(false);

  const openAttestation = useCallback(async (job) => {
    setAttestJob(job);
    setAttestChecked(false);
    if (!attestText) {
      try {
        const res = await CandidateService.fetchJobFitApplyConsentText();
        setAttestText(res?.consent_text || "");
      } catch (err) {
        console.error("Failed to fetch job-fit apply consent text:", err);
        setAttestText(
          "I confirm that I am authorized to submit this candidate's application to this job on their behalf.",
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeAttestation = useCallback(() => {
    if (attestSubmitting) return;
    setAttestJob(null);
  }, [attestSubmitting]);

  // Confirming the attestation is consent #1 — it does not itself apply
  // anything. It navigates to the review route, which is where the recruiter's
  // edits and the actual submit happen. `attested: true` in route state is
  // what that route checks before rendering — see apply-to-job/index.jsx —
  // so there is no way to reach the form (a direct URL, a bookmark, a reload)
  // without having ticked this box first.
  const confirmAttestation = useCallback(() => {
    if (!attestJob || !attestChecked) return;
    const job = attestJob;
    setAttestSubmitting(true);
    setAttestJob(null);
    navigate(`/admin/candidate-analysis/${candidateId}/apply-to-job/${job.job_id}`, {
      state: {
        attested: true,
        jobTitle: job.job_title,
        // Carries the `?job_id=...` this tab is rendered under — the
        // candidate-analysis page only fetches and renders once both
        // candidateId AND job_id are present, so a bare candidateId path
        // here would land back on a page stuck on its skeleton loaders.
        fromPath: `${location.pathname}${location.search}`,
      },
    });
    setAttestSubmitting(false);
  }, [attestJob, attestChecked, candidateId, location, navigate]);

  // Guards against a slow in-flight request landing after the component
  // unmounted or the candidate changed.
  const activeCandidateRef = useRef(candidateId);
  useEffect(() => {
    activeCandidateRef.current = candidateId;
  }, [candidateId]);

  const fetchResults = useCallback(
    async ({ silent = false } = {}) => {
      if (!candidateId) return;
      if (!silent) setLoading(true);
      try {
        const res = await CandidateService.fetchJobFitResults(candidateId);
        if (activeCandidateRef.current !== candidateId) return;
        setFitData(res?.data || null);
        setFetchError(null);
      } catch (err) {
        if (activeCandidateRef.current !== candidateId) return;
        // A failed background poll must not wipe the results already on screen.
        if (!silent) setFetchError(err?.response?.data?.message || "Failed to load job-fit results");
      } finally {
        if (activeCandidateRef.current === candidateId && !silent) setLoading(false);
      }
    },
    [candidateId],
  );

  useEffect(() => {
    setFitData(null);
    setFetchError(null);
    setLevelFilter("all");
    fetchResults();
  }, [fetchResults]);

  const status = fitData?.status || "NOT_STARTED";
  const isStale = Boolean(fitData?.is_stale);
  const isRunning = status === "IN_PROGRESS";

  // One subscription for all three job-fit events. `job_fit:<candidate>` is
  // this candidate, so nothing narrows further.
  //
  // `onChange` also fires on a reconnect and on a detected gap: events fired
  // while the socket was down are not replayed, and without that a run that
  // finished during a blip would leave the tab on its spinner for ever.
  useLiveResource({
    keys: `job_fit:${candidateId}`,
    enabled: Boolean(candidateId),
    onEvent: (event) => {
      // The status the refetch cannot show yet.
      if (event.event === "job_fit.started") {
        setFitData((prev) => ({ ...(prev || {}), status: "IN_PROGRESS", error: null }));
      }
    },
    onChange: () => fetchResults({ silent: true }),
  });

  const handleAnalyze = async () => {
    if (!candidateId || triggering) return;
    setTriggering(true);
    setFetchError(null);
    try {
      const res = await CandidateService.triggerJobFitAnalysis(candidateId);
      setFitData((prev) => ({ ...(prev || {}), status: "IN_PROGRESS", error: null, ...(res?.data || {}) }));
      toast.success(res?.message || "Analysis started");
    } catch (err) {
      const errorCode = err?.response?.data?.error_code;
      const message = err?.response?.data?.message;
      if (errorCode === "JOB_FIT_IN_PROGRESS") {
        setFitData((prev) => ({ ...(prev || {}), status: "IN_PROGRESS", error: null }));
        toast.success("Analysis is already in progress");
      } else if (errorCode === "JOB_FIT_NO_DATA") {
        toast.error(message || "This candidate has no confirmed application data to analyze");
        fetchResults({ silent: true });
      } else {
        toast.error(message || "Failed to start analysis");
        fetchResults({ silent: true });
      }
    } finally {
      setTriggering(false);
    }
  };

  const sortedResults = useMemo(() => {
    const results = fitData?.results || [];
    return [...results].sort((a, b) => (Number(b.fit_score) || 0) - (Number(a.fit_score) || 0));
  }, [fitData?.results]);

  const counts = useMemo(() => {
    const next = { all: sortedResults.length, high: 0, medium: 0, low: 0 };
    for (const job of sortedResults) next[resolveLevel(job)] += 1;
    return next;
  }, [sortedResults]);

  const visibleResults = useMemo(
    () => (levelFilter === "all" ? sortedResults : sortedResults.filter((job) => resolveLevel(job) === levelFilter)),
    [sortedResults, levelFilter],
  );

  const formatAnalyzedAt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const analyzedAtLabel = status === "COMPLETED" ? formatAnalyzedAt(fitData?.analyzed_at) : null;

  const renderAction = () => {
    // Nothing while a run is in flight — the in-progress card below already
    // shows a spinner, and a second one in the header just reads as a bug.
    if (isRunning) return null;

    if (loading && !fitData) return null;

    // Not in the tenant's plan: no control and no explanation. They are here
    // only to read a stored analysis, and a sentence about a feature they do
    // not have is an advert, not information.
    if (!moduleEnabled) return null;

    // A Reanalyze button is offered even when the results are fresh: hiding it
    // leaves no way to re-run after editing a job or a resume in a way the
    // staleness check doesn't catch.
    //
    // No action control at all while the module is dark, and no wording here
    // either: the banner below the header says it once. This spot used to
    // return the sentence, and the NOT_STARTED empty state returned the same
    // sentence, so a dark module with no stored analysis printed it twice.
    if (!moduleAvailable) return null;

    const label = status === "NOT_STARTED" ? "Analyze" : status === "FAILED" ? "Retry" : "Reanalyze";
    const Icon = status === "NOT_STARTED" ? null : RefreshCw;

    return (
      <Button className={ACTION_BUTTON_CLASS} onClick={handleAnalyze} disabled={triggering}>
        {triggering ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : Icon && <Icon size={14} aria-hidden="true" />}
        {label}
      </Button>
    );
  };

  const renderBody = () => {
    if (loading && !fitData) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={20} className="animate-spin text-text-secondary" aria-hidden="true" />
        </div>
      );
    }

    if (fetchError) {
      return <EmptyState icon={AlertCircle} tone="danger" title="Failed to load data" body={fetchError} />;
    }

    if (status === "NOT_STARTED") {
      // Three cases, three sentences, and none of them repeats the banner:
      // not entitled has no button to press and no outage to wait out; dark
      // fleet-wide already has the banner above, so this only says the Analyze
      // button is not there; available invites the user to press it.
      return (
        <EmptyState
          icon={Briefcase}
          title="No analysis yet"
          body={
            !moduleEnabled
              ? "This candidate has no stored job-fit analysis."
              : moduleAvailable
                ? "Analyze this candidate against all open jobs to see which positions they fit best."
                : "New analyses are paused, so there is nothing to show for this candidate yet."
          }
        />
      );
    }

    if (isRunning) {
      return (
        <Card className="border-border-primary p-6">
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
            <Loader2 size={24} className="animate-spin text-accent-text" aria-hidden="true" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-text-primary">Analyzing candidate fit</p>
              <p className="text-xs text-text-secondary">Scoring against all open jobs. This may take a few minutes.</p>
            </div>
          </div>
        </Card>
      );
    }

    if (status === "FAILED") {
      return (
        <Card className="border-tone-rose/30 bg-tone-rose-soft p-6">
          <EmptyState icon={AlertCircle} tone="danger" title="Analysis failed" body={fitData?.error || "An unexpected error occurred during analysis."} />
        </Card>
      );
    }

    if (!sortedResults.length) {
      return (
        <Card className="border-border-primary p-6">
          <EmptyState
            icon={Briefcase}
            title="No matching jobs found"
            body="No open jobs could be scored for this candidate. Jobs may have been closed since the last run."
          />
        </Card>
      );
    }

    return (
      <div className="space-y-3">
        {isStale && (
          <div className="flex items-start gap-2 rounded-lg border border-tone-amber/40 bg-tone-amber-soft px-3 py-2 text-xs font-medium text-text-primary">
            <AlertCircle size={14} className="mt-px shrink-0 text-tone-amber" aria-hidden="true" />
            Jobs or this candidate's details changed since the last analysis. Reanalyze for up-to-date results.
          </div>
        )}

        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by fit level">
          {["all", ...LEVELS].map((key) => {
            const active = levelFilter === key;
            const disabled = key !== "all" && counts[key] === 0;
            return (
              <Button
                key={key}
                onClick={() => setLevelFilter(key)}
                disabled={disabled}
                aria-pressed={active}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  active
                    ? "border-border-primary bg-background-tertiary font-semibold text-text-primary"
                    : "border-border-primary font-medium text-text-secondary hover:text-text-primary"
                } ${disabled ? "cursor-not-allowed opacity-50 hover:text-text-secondary" : ""}`}
              >
                {key === "all" ? "All" : LEVEL_LABEL[key].replace(" fit", "")} {counts[key]}
              </Button>
            );
          })}
        </div>

        {visibleResults.length === 0 ? (
          <p className="py-8 text-center text-xs text-text-secondary">No jobs in this band.</p>
        ) : (
          visibleResults.map((job) => (
            <JobFitCard key={job.job_id} job={job} canApply={canApply} onApply={openAttestation} />
          ))
        )}
      </div>
    );
  };

  // Not in this tenant's plan — the tab renders nothing at all.
  return (
    // Shell clips, inner pane scrolls — the same shape the other tabs use.
    //
    // `[contain:paint]` on the scrolling pane is load-bearing, not decoration.
    // This page's root sets `zoom: 0.9`, and inside a zoomed subtree Chrome
    // computes the *document's* scrollable area from this pane's unclipped
    // content height (1475px) rather than its own clipped height (749px) — even
    // though every ancestor reports no overflow. The result was a second,
    // page-level scrollbar on the candidate page whenever the list was long
    // enough to scroll. Paint containment stops the overflow escaping; the
    // pane's own scrolling is unaffected.
    <div className="flex h-full flex-col overflow-hidden">
      <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto p-4 [contain:paint]">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-text-primary">Suitable Jobs</h2>
            {analyzedAtLabel && (
              <p className="mt-0.5 text-xs text-text-secondary">
                Last analyzed {analyzedAtLabel}
                {fitData?.jobs_total != null && ` · ${fitData.jobs_total} ${fitData.jobs_total === 1 ? "job" : "jobs"} evaluated`}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">{renderAction()}</div>
        </div>

        {/* One banner, the same shape the interview tab uses. It renders only
            for a tenant that owns the module — the notice checks that itself —
            so a tenant reading a stored analysis for a module they never bought
            is not told to wait for an outage that has nothing to do with them. */}
        <ModuleUnavailableNotice module={MODULES.AI_CANDIDATE_JOB_FIT} suffix="Any stored analysis is shown below." className="mb-3" />

        {renderBody()}
      </div>

      <AttestationDialog
        job={attestJob}
        text={attestText}
        checked={attestChecked}
        submitting={attestSubmitting}
        onCheckedChange={setAttestChecked}
        onCancel={closeAttestation}
        onConfirm={confirmAttestation}
      />
    </div>
  );
};

export default JobFit;
